import { getProductVariants } from './productVariants';
import { productExportWeight } from './productExportWeight';

export const KOREA_EXPORT_COLUMNS = Object.freeze([
  ['image', '圖片', 20], ['itemNumber', '料號', 20], ['name', '品名', 32],
  ['styles', '樣式（合併顯示）', 28], ['salePrice', '商品原始售價', 22, true], ['remarks', '備註', 40],
  ['costKRW', '韓幣成本（KRW）', 20, true], ['costTWD', '台幣成本（TWD）', 20, true],
  ['adjustedTWD', '台幣換算＋額外10%（TWD）', 26, true],
  ['airTWD', '直飛空運（TWD）', 22, true], ['seaTWD', '海快（TWD）', 22, true],
  ['transitTWD', '轉機空運（TWD）', 22, true],
].map(([key, label, width, numeric = false]) => Object.freeze({ key, label, width, numeric })));

const present = v => v !== undefined && v !== null && String(v).trim() !== '';
// Empty cells remain empty. Never coerce booleans, currency labels or formulas.
export const exportNumber = v => (typeof v === 'number' || typeof v === 'string') && present(v) && Number.isFinite(Number(v)) ? Number(v) : null;
const inherit = (p, v, key) => present(v?.[key]) ? v[key] : p[key];
export const koreaSalePrice = (product, variants = []) => {
  const values = [...new Set((variants.length ? variants : [{}]).flatMap(v => ['livePrice', 'onlinePrice'].map(key => exportNumber(inherit(product, v, key)))).filter(v => v !== null))];
  return values.length > 1 ? values.join('／') : values[0] ?? null;
};
const costKeys = ['costKoreanWon', 'costTWD', 'weight'];
const sourceFor = (entry, variant) => {
  const p = entry.product || {};
  if (!p.subProducts?.length) return Object.fromEntries(costKeys.map(k => [k, inherit(p, variant, k)]));
  // Use the same explicit style / first-style selection as the detail page for
  // child references, but never substitute parent costs for missing child data.
  const totals = Object.fromEntries(costKeys.map(k => [k, null]));
  const complete = Object.fromEntries(costKeys.map(k => [k, true]));
  for (const [i, ref] of p.subProducts.entries()) {
    const child = entry.detailContext?.subProducts?.[i]?.product;
    const quantity = present(ref.quantity) ? exportNumber(ref.quantity) : 1;
    if (!child || quantity === null || quantity < 0) return {};
    const variants = getProductVariants(child);
    const selected = ref.style && ref.style !== '--' ? variants.find(v => v.name === ref.style || v.style === ref.style) : variants[0];
    for (const key of costKeys) {
      const value = exportNumber(inherit(child, selected, key));
      if (value === null) complete[key] = false;
      else totals[key] = (totals[key] ?? 0) + value * quantity;
    }
  }
  return Object.fromEntries(costKeys.map(k => [k, complete[k] ? totals[k] : null]));
};

export const koreaNumericValues = (entry, variant = {}) => {
  const source = sourceFor(entry, variant), rates = entry.detailContext?.rates?.data || {};
  const krw = exportNumber(source.costKoreanWon), twd = exportNumber(source.costTWD), weight = exportNumber(source.weight);
  const rate = exportNumber(rates.exchangeRate), validRate = rate !== null && rate > 0;
  const foreignTWD = krw !== null && validRate ? krw / rate : null;
  const conversionBase = present(source.costKoreanWon) ? foreignTWD : twd;
  const shippingBase = present(source.costTWD) ? twd : foreignTWD;
  const extra = exportNumber(rates.extraPercentage);
  const result = { salePrice: koreaSalePrice(entry.product || {}, [variant]), costKRW: krw,
    costTWD: present(source.costTWD) ? twd : foreignTWD === null ? null : Math.ceil(foreignTWD),
    adjustedTWD: conversionBase === null ? null : Math.ceil(conversionBase * 1.1) };
  for (const [mode, legacy] of [['air', 'airRate'], ['sea', 'seaRate'], ['transit', 'transitRate']]) {
    const local = exportNumber(rates[`${mode}TaiwanPerKg`] ?? rates[legacy]);
    const international = exportNumber(rates[`${mode}IntlKrwPerKg`]);
    result[`${mode}TWD`] = shippingBase === null || weight === null || weight < 0 || extra === null || local === null || international === null || !validRate
      ? null : Math.ceil(shippingBase * (1 + extra / 100) + weight * local + weight * international / rate);
  }
  return Object.fromEntries(Object.entries(result).map(([key, value]) => [key, key === 'salePrice' || Number.isFinite(value) ? value : null]));
};

export const groupKoreaExportRows = rows => rows.flatMap((entry, sourceIndex) => {
  const p = entry.product || {};
  if ((entry.country || p.country) !== 'korea') throw new Error('韓國簡版僅適用韓國商品');
  const variants = getProductVariants(p), ids = new Set(entry.selectedVariantIds || []);
  const chosen = variants.filter(v => ids.has(v.id));
  if (variants.length && (!chosen.length || [...ids].some(id => !variants.some(v => v.id === id)))) throw new Error('請重新確認已選樣式後匯出韓國簡版');
  const groups = new Map();
  const productWeight = productExportWeight(p, chosen, entry.detailContext);
  const salePrice = koreaSalePrice(p, chosen);
  for (const variant of chosen.length ? chosen : [{}]) {
    const numeric = { ...koreaNumericValues(entry, variant), salePrice };
    const weight = exportNumber(sourceFor(entry, variant).weight);
    const key = JSON.stringify({ numeric, weight });
    if (!groups.has(key)) groups.set(key, { ...entry, selectedVariantIds: [], koreaNumeric: numeric, koreaWeight: productWeight.value, koreaWeightIssue: productWeight.issue, koreaWeightGroup: sourceIndex });
    if (variant.id) groups.get(key).selectedVariantIds.push(variant.id);
  }
  return [...groups.values()];
});
